import { SearchOutlined } from "@ant-design/icons";
import { Input, Table } from "antd";
import { createUseStyles } from "react-jss";
import {
  forwardRef,
  useCallback,
  useImperativeHandle, useEffect,
  useMemo,
  useState,
} from "react";
import useCustomerManagementApi, { CUSTOMER_QUERIES } from "../../apiHooks/useCustomerManagementApi";
import { customerManagementColumns } from "../config/customerManagement-columns";

const useStyles = createUseStyles({
  wrapper: { display: "flex", flexDirection: "column", gap: 20 },

  filterBar: {
    background: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e4edf6",
    boxShadow: "0 2px 12px rgba(27,42,107,0.06)",
    padding: "12px 16px",
  },
  searchInput: {
    "&": {
      borderRadius: "10px !important",
      height: "36px !important",
      background: "#f0f6fb !important",
      border: "1px solid #dde8f4 !important",
      boxShadow: "none !important",
      display: "flex !important",
      alignItems: "center !important",
      width: "100% !important",
      fontSize: "0.875rem !important",
    },
    "&:focus-within": {
      borderColor: "#00A9E0 !important",
      boxShadow: "0 0 0 2px rgba(0,169,224,0.12) !important",
    },
    "& .ant-input": {
      background: "transparent !important",
      fontSize: "0.875rem !important",
    },
  },

  tableCard: {
    background: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e4edf6",
    boxShadow: "0 2px 12px rgba(27,42,107,0.06)",
    overflow: "hidden",
  },
  tableWrap: {
    "& .ant-table-thead > tr > th": {
      background: "#f5f9fd !important",
      color: "#1B2A6B !important",
      fontWeight: "700 !important",
      fontSize: "0.78rem !important",
      letterSpacing: "0.04em !important",
      textTransform: "uppercase !important",
      borderTop: "1px solid #e4edf6 !important",
      borderBottom: "1px solid #e4edf6 !important",
      borderRadius: "0 !important",
      padding: "12px 14px !important",
    },
    "& .ant-table-thead > tr > th:first-child, & .ant-table-thead > tr > th:last-child": {
      borderRadius: "0 !important",
    },
    "& .ant-table-tbody > tr > td": {
      borderBottom: "1px solid #f0f5fb !important",
      padding: "10px 14px !important",
      fontSize: "0.875rem !important",
      color: "#1a1d2e !important",
    },
    "& .ant-table-tbody > tr:hover > td": { background: "#f7fbff !important" },
    "& .ant-table-tbody > tr:nth-child(even) > td": { background: "#fafcff" },
    "& .ant-table-tbody > tr:nth-child(even):hover > td": { background: "#f7fbff !important" },
    "& .ant-table": { border: "none !important" },
    "& .ant-table-container": { border: "none !important" },
    "& .ant-pagination": {
      padding: "10px 20px !important",
      margin: "0 !important",
      borderTop: "1px solid #edf3fa !important",
    },
  },
});

const CustomerManagementTable = forwardRef(({ actionCellRenderer }, ref) => {
  const classes = useStyles();
  const [filterData, setFilterData] = useState({ search: null });

  const {
    listCustomersApi: { data, execute, isLoading },
  } = useCustomerManagementApi([CUSTOMER_QUERIES.listCustomers]);

  useImperativeHandle(ref, () => ({ refresh() { execute(); } }));

  const handleFilterData = useCallback((key, val) => {
    setFilterData((d) => ({ ...d, [key]: val }));
  }, []);

  const filteredData = useMemo(() => {
    const norm = filterData.search?.trim().toLowerCase() || "";
    return data.filter((x) => {
      if (!norm) return true;
      return (
        x.firstName?.toLowerCase().includes(norm) ||
        x.lastName?.toLowerCase().includes(norm) ||
        x.email?.toLowerCase().includes(norm) ||
        x.mobileNo?.toLowerCase().includes(norm) ||
        x.nic?.toLowerCase().includes(norm)
      );
    });
  }, [data, filterData.search]);

  return (
    <div className={classes.wrapper}>

      <div className={classes.filterBar}>
        <Input
          className={classes.searchInput}
          onChange={(e) => handleFilterData("search", e.currentTarget.value)}
          placeholder="Search by name, email, NIC or contact..."
          prefix={<SearchOutlined style={{ color: "#9ab8cf" }} />}
          value={filterData.search}
          allowClear
        />
      </div>

      <div className={classes.tableCard}>
        <div className={classes.tableWrap}>
          <Table
            size="small"
            columns={customerManagementColumns(actionCellRenderer)}
            dataSource={filteredData}
            loading={isLoading}
            scroll={{ x: "max-content" }}
            pagination={{
              defaultPageSize: 10,
              pageSizeOptions: [5, 10, 15, 20, 25],
              showSizeChanger: true,
              position: ["bottomRight"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </div>
      </div>

    </div>
  );
});

export default CustomerManagementTable;
